import { useState, useEffect } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  priority?: boolean;
}

const OptimizedImage = ({ src, alt, className = "", width, height, priority = false }: OptimizedImageProps) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    console.log('OptimizedImage: Loading image:', src);
    
    const img = new Image();
    let isMounted = true;

    const loadImage = async () => {
      try {
        if (src.includes('lovable-uploads')) {
          const response = await fetch(src);
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          
          if (!isMounted) return;
          
          img.onload = () => {
            if (!isMounted) return;

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d', { alpha: true });
            
            if (!ctx) {
              throw new Error('Canvas context not available');
            }

            // Calculate dimensions while preserving aspect ratio
            const aspectRatio = img.naturalWidth / img.naturalHeight;
            let targetWidth = width || img.naturalWidth;
            let targetHeight = height || img.naturalHeight;

            if (width && height) {
              const containerAspectRatio = width / height;
              if (containerAspectRatio > aspectRatio) {
                targetWidth = targetHeight * aspectRatio;
              } else {
                targetHeight = targetWidth / aspectRatio;
              }
            }

            canvas.width = targetWidth;
            canvas.height = targetHeight;

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // Draw image with proper scaling
            ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
            
            canvas.toBlob(
              (blob) => {
                if (blob && isMounted) {
                  const optimizedUrl = URL.createObjectURL(blob);
                  setImageUrl(optimizedUrl);
                  setIsLoading(false);
                }
              },
              'image/webp',
              0.98 // Higher quality setting for better clarity
            );
          };

          img.src = objectUrl;
        } else {
          setImageUrl(src);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error optimizing image:', err);
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Failed to load image'));
          setImageUrl(src); // Fallback to original image
          setIsLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
      if (imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [src, width, height]);

  if (error) {
    console.error('OptimizedImage: Error loading image:', error);
  }

  return (
    <>
      {isLoading && (
        <div className={`animate-pulse bg-gray-200 ${className}`} style={{ width, height }} />
      )}
      <img
        src={imageUrl || src}
        alt={alt}
        className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        onError={() => {
          console.error('OptimizedImage: Image failed to load:', src);
          setError(new Error('Failed to load image'));
          setIsLoading(false);
        }}
      />
    </>
  );
};

export default OptimizedImage;